/**
 * Create a JSON Patch Document for Submission updates
 * Follows RFC 6902 JSON Patch format for ASP.NET Core JsonPatchDocument
 *
 * @param {Object} updates - Object containing the fields to update
 * @returns {Array} Array of patch operations
 *
 * @example
 * const patchDoc = createSubmissionPatchDocument({
 *   status: 3, // CertificationStatusEnum.Pending
 *   score: 85,
 *   name: "Q1 2024 Certification"
 * });
 * // Returns: [
 * //   { op: "replace", path: "/Status", value: 3 },
 * //   { op: "replace", path: "/Score", value: 85 },
 * //   { op: "replace", path: "/Name", value: "Q1 2024 Certification" }
 * // ]
 */
export const createSubmissionPatchDocument = updates => {
  const patchOperations = [];

  // Map of frontend field names to backend C# property names
  const fieldMapping = {
    name: 'Name',
    status: 'Status',
    score: 'Score',
    userId: 'User_Id',
    dealerId: 'Dealer_Id',
    criteriaId: 'Criteria_Id',
    certificationType: 'CertificationType',
    dateIssued: 'DateIssued',
    heroUnitProductLineId: 'HeroUnitProductLine_Id',
    attemptedCriteriaOptionId: 'AttemptedCriteriaOption_Id',
    approvedCriteriaOptionId: 'ApprovedCriteriaOption_Id',
  };

  Object.keys(updates).forEach(key => {
    const backendPath = fieldMapping[key] || key;
    const value = updates[key];

    if (value !== undefined && value !== null) {
      patchOperations.push({
        op: 'replace',
        path: `/${backendPath}`,
        value: value,
      });
    }
  });

  return patchOperations;
};

/**
 * Create a JSON Patch Document for adding a new SubmissionDetail
 *
 * @param {Object} submissionDetail - The submission detail to add
 * @returns {Array} Array of patch operations
 *
 * @example
 * const patchDoc = createAddSubmissionDetailPatch({
 *   description: "Photo of retail environment",
 *   criteriaTypeDetailId: "detail-123"
 * });
 */
export const createAddSubmissionDetailPatch = submissionDetail => {
  return [
    {
      op: 'add',
      path: '/SubmissionDetails/-',
      value: {
        Id: crypto.randomUUID ? crypto.randomUUID() : generateUUID(),
        Description: submissionDetail.description || '',
        CriteriaTypeDetail_Id: submissionDetail.criteriaTypeDetailId,
      },
    },
  ];
};

/**
 * Create a JSON Patch Document for updating an existing SubmissionDetail
 *
 * @param {number} index - Index of the submission detail in the array
 * @param {Object} updates - Fields to update
 * @returns {Array} Array of patch operations
 *
 * @example
 * const patchDoc = createUpdateSubmissionDetailPatch(0, {
 *   description: "Updated description"
 * });
 */
export const createUpdateSubmissionDetailPatch = (index, updates) => {
  const patchOperations = [];

  if (updates.description !== undefined) {
    patchOperations.push({
      op: 'replace',
      path: `/SubmissionDetails/${index}/Description`,
      value: updates.description,
    });
  }

  if (updates.criteriaTypeDetailId !== undefined) {
    patchOperations.push({
      op: 'replace',
      path: `/SubmissionDetails/${index}/CriteriaTypeDetail_Id`,
      value: updates.criteriaTypeDetailId,
    });
  }

  return patchOperations;
};

/**
 * Create a JSON Patch Document for removing a SubmissionDetail
 *
 * @param {number} index - Index of the submission detail to remove
 * @returns {Array} Array of patch operations
 */
export const createRemoveSubmissionDetailPatch = index => {
  return [
    {
      op: 'remove',
      path: `/SubmissionDetails/${index}`,
    },
  ];
};

/**
 * Helper function to generate UUID for browsers that don't support crypto.randomUUID
 */
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Combine multiple patch documents into one
 *
 * @param {...Array} patchDocs - Multiple patch document arrays
 * @returns {Array} Combined patch operations
 *
 * @example
 * const statusPatch = createSubmissionPatchDocument({ status: 3 });
 * const detailPatch = createAddSubmissionDetailPatch({ description: "Test" });
 * const combined = combinePatchDocuments(statusPatch, detailPatch);
 */
export const combinePatchDocuments = (...patchDocs) => {
  return patchDocs.flat();
};

export default {
  createSubmissionPatchDocument,
  createAddSubmissionDetailPatch,
  createUpdateSubmissionDetailPatch,
  createRemoveSubmissionDetailPatch,
  combinePatchDocuments,
};
